import { httpsCallable } from 'firebase/functions';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import {
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  CircleCheckBig,
  Clock3,
  FileSignature,
  Pencil,
  Search,
  Trash2,
  UserRound,
  UsersRound,
  X
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import { db, functions } from '../../core/firebase/firebase';
import { useAuth } from '../../core/auth/AuthProvider';
import { PERMISSIONS } from '../../core/permissions/roles';

const text = (value) => String(value ?? '').trim();

const parentName = (parent) =>
  text(parent?.fullName || parent?.fullname) || 'Parent sans nom';

const studentName = (student) =>
  text(student?.fullName || student?.fullname) || 'Élève sans nom';

const studentLevel = (student) =>
  text(
    student?.level ||
      student?.requestedLevel ||
      student?.classLabel
  ) || 'Classe non affectée';


const SCHOOL_YEAR = '2026-2027';

const dateValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const contractStatusMeta = (contract) => {
  if (!contract) {
    return {
      label: 'Contrat non créé',
      action: 'Créer le contrat',
      tone: 'missing',
      Icon: CircleAlert
    };
  }

  if (contract.status === 'accepted' || contract.accepted === true) {
    return {
      label: 'Contrat accepté',
      action: 'Voir le contrat',
      tone: 'accepted',
      Icon: CircleCheckBig
    };
  }

  if (
    contract.status === 'sent_to_parent' ||
    contract.status === 'pending_parent_acceptance'
  ) {
    return {
      label: 'À valider par le parent',
      action: 'Consulter le contrat',
      tone: 'waiting',
      Icon: Clock3
    };
  }

  if (
    contract.status === 'pending_manager_approval' ||
    contract.discountStatus === 'pending'
  ) {
    return {
      label: 'Validation gérante requise',
      action: 'Ouvrir le contrat',
      tone: 'approval',
      Icon: Clock3
    };
  }

  if (contract.status === 'discount_rejected') {
    return {
      label: 'Remise refusée',
      action: 'Corriger le contrat',
      tone: 'rejected',
      Icon: CircleAlert
    };
  }

  return {
    label: 'Contrat en brouillon',
    action: 'Continuer le contrat',
    tone: 'draft',
    Icon: FileSignature
  };
};

export default function ParentsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canManageParent = can(PERMISSIONS.PARENTS_MANAGE);
  const canAdminParentAccount = can(PERMISSIONS.PARENTS_ACCOUNT_ADMIN);
  const canManageContracts = can(PERMISSIONS.FINANCE_GLOBAL_VIEW);

  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    cin: '',
    profession: '',
    address: '',
    activationStatus: 'pending_sms'
  });

  useEffect(() => {
    const offParents = onSnapshot(
      collection(db, 'parents'),
      (snapshot) => {
        const rows = snapshot.docs
          .map((item) => ({
            id: item.id,
            ...item.data()
          }))
          .sort((a, b) =>
            parentName(a).localeCompare(parentName(b), 'fr')
          );

        setParents(rows);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError(
          'Impossible de charger les parents. Réessayez ou vérifiez vos droits d’accès.'
        );
        setLoading(false);
      }
    );

    const offStudents = onSnapshot(
      collection(db, 'students'),
      (snapshot) => {
        setStudents(
          snapshot.docs.map((item) => ({
            id: item.id,
            ...item.data()
          }))
        );
      },
      (err) => {
        console.error(err);
        setError('Impossible de charger les élèves liés.');
      }
    );

    const offContracts = onSnapshot(
      collection(db, 'contracts'),
      (snapshot) => {
        setContracts(
          snapshot.docs.map((item) => ({
            id: item.id,
            ...item.data()
          }))
        );
      },
      (err) => {
        console.error(err);
        setError('Impossible de charger les statuts des contrats.');
      }
    );

    return () => {
      offParents();
      offStudents();
      offContracts();
    };
  }, []);

  const selected =
    parents.find((parent) => parent.id === selectedId) || null;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return parents.filter((parent) => {
      if (!needle) return true;

      return [
        parentName(parent),
        parent.phone,
        parent.email,
        parent.cin
      ].some((value) =>
        text(value).toLowerCase().includes(needle)
      );
    });
  }, [parents, search]);

  const linkedChildren = useMemo(() => {
    if (!selected) return [];

    return students.filter(
      (student) =>
        student.primaryParentId === selected.id ||
        student.parentId === selected.id ||
        (Array.isArray(student.parentIds) &&
          student.parentIds.includes(selected.id))
    );
  }, [selected, students]);

  const latestContractForStudent = (studentId) =>
    contracts
      .filter(
        (contract) =>
          contract.studentId === studentId &&
          (!contract.schoolYear || contract.schoolYear === SCHOOL_YEAR)
      )
      .sort(
        (a, b) =>
          Math.max(dateValue(b.updatedAt), dateValue(b.createdAt)) -
          Math.max(dateValue(a.updatedAt), dateValue(a.createdAt))
      )[0] || null;

  const familyReadiness = useMemo(() => {
    if (!linkedChildren.length) {
      return {
        accepted: 0,
        total: 0,
        label: 'Aucun enfant lié',
        tone: 'empty'
      };
    }

    const accepted = linkedChildren.filter((child) => {
      const contract = latestContractForStudent(child.id);
      return contract?.status === 'accepted' || contract?.accepted === true;
    }).length;

    if (accepted === linkedChildren.length) {
      return {
        accepted,
        total: linkedChildren.length,
        label: 'Famille prête',
        tone: 'ready'
      };
    }

    if (accepted > 0) {
      return {
        accepted,
        total: linkedChildren.length,
        label: 'Famille partiellement prête',
        tone: 'partial'
      };
    }

    const created = linkedChildren.some((child) =>
      Boolean(latestContractForStudent(child.id))
    );

    return {
      accepted,
      total: linkedChildren.length,
      label: created ? 'Préparation en cours' : 'Famille non préparée',
      tone: created ? 'progress' : 'missing'
    };
  }, [linkedChildren, contracts]);

  function openEdit() {
    if (!selected) return;

    setForm({
      fullName:
        parentName(selected) === 'Parent sans nom'
          ? ''
          : parentName(selected),
      phone: selected.phone || '',
      email: selected.email || '',
      cin: selected.cin || '',
      profession: selected.profession || '',
      address: selected.address || '',
      activationStatus:
        selected.activationStatus || 'pending_sms'
    });

    setEditing(true);
    setError('');
  }

  async function saveParent(event) {
    event.preventDefault();

    if (!selected || !canManageParent || !form.fullName.trim()) return;

    setSaving(true);
    setError('');

    try {
      const payload = {
        fullName: form.fullName.trim(),
        fullname: form.fullName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        cin: form.cin.trim(),
        profession: form.profession.trim(),
        address: form.address.trim(),
        updatedAt: serverTimestamp()
      };

      if (canAdminParentAccount) {
        payload.activationStatus = form.activationStatus;
      }

      await updateDoc(doc(db, 'parents', selected.id), payload);

      setEditing(false);
      setMessage('Dossier parent modifié.');

      window.setTimeout(() => {
        setMessage('');
      }, 1800);
    } catch (err) {
      console.error(err);
      setError(`Modification impossible : ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function deleteParent() {
  if (!selected) return;

  const parentUid =
    selected.parentUserUid ||
    selected.userUid ||
    selected.authUid ||
    '';

  if (!parentUid) {
    setError(
      'Impossible de supprimer ce parent : aucun compte Firebase lié.'
    );
    return;
  }

  const confirmed = window.confirm(
    `Supprimer définitivement ${parentName(selected)} et ses données ?\n\n` +
    `${linkedChildren.length} enfant(s) lié(s) seront également supprimé(s) ` +
    `s’ils n’ont pas d’autre parent.\n\n` +
    `Les contrats, paiements, notifications et réclamations liés seront aussi supprimés.\n\n` +
    `Cette action est irréversible.`
  );

  if (!confirmed) return;

  setSaving(true);
  setError('');

  try {
    const manageAccount = httpsCallable(
      functions,
      'manageAccount'
    );
    

const result = await manageAccount({
  action: 'delete',
  uid: parentUid,
  parentId: selected.id,
  deleteLinkedData: true
});

console.log('DELETE RESULT:', result.data);

    setSelectedId(null);

    setMessage(
      'Parent et données associées supprimés.'
    );

    window.setTimeout(() => {
      setMessage('');
    }, 2500);
  } catch (err) {
    console.error('Suppression parent :', err);

    setError(
      err?.message ||
      'Impossible de supprimer le parent.'
    );
  } finally {
    setSaving(false);
  }
}

  function openContract(child, contract = null) {
    if (!selected || !child?.id) return;

    const params = new URLSearchParams({
      parentId: selected.id,
      studentId: child.id
    });

    if (contract?.id) {
      params.set('contractId', contract.id);
    }

    setSelectedId(null);
    navigate(`/finance?${params.toString()}`);
  }

  function activationLabel(parent) {
    switch (parent.activationStatus) {
      case 'active':
        return 'Actif';
      case 'sms_sent':
        return 'SMS envoyé';
      case 'pending_sms':
        return 'SMS à envoyer';
      case 'invited':
        return 'Invité';
      default:
        return 'Non préparé';
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="MODULE"
        title="Parents"
        description="Consultez et modifiez les responsables légaux et les enfants liés à chaque famille."
      />

      {error && (
        <div className="dashboard-alert">
          <div>
            <strong>Une erreur est survenue.</strong>
            <span>{error}</span>
          </div>
        </div>
      )}

      {message && (
        <div className="success-note task-toast">
          <CheckCircle2 size={17} />
          {message}
        </div>
      )}

      <section className="student-toolbar panel parent-toolbar">
        <label className="student-search">
          <Search size={18} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un parent, téléphone, CIN…"
          />
        </label>
      </section>

      <section className="panel student-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">FAMILLES</p>
            <h2>
              {visible.length} parent
              {visible.length > 1 ? 's' : ''}
            </h2>
          </div>

          <span className="live-badge">
            <span />
            Mise à jour automatique
          </span>
        </div>

        {loading ? (
          <div className="compact-empty">Chargement…</div>
        ) : (
          <div className="student-list">
            {visible.map((parent) => {
              const count = students.filter(
                (student) =>
                  student.primaryParentId === parent.id ||
                  student.parentId === parent.id ||
                  (Array.isArray(student.parentIds) &&
                    student.parentIds.includes(parent.id))
              ).length;

              return (
                <button
                  type="button"
                  className="student-row parent-row"
                  key={parent.id}
                  onClick={() => setSelectedId(parent.id)}
                >
                  <span className="student-avatar">
                    <UserRound size={18} />
                  </span>

                  <span className="student-main">
                    <strong>{parentName(parent)}</strong>
                    <small>
                      {parent.phone || 'Téléphone non renseigné'}
                    </small>
                  </span>

                  <span className="student-parent">
                    <UsersRound size={15} />
                    {count} enfant{count > 1 ? 's' : ''}
                  </span>

                  <span
                    className={`student-status ${
                      parent.status === 'archived'
                        ? 'archived'
                        : 'active'
                    }`}
                  >
                    {activationLabel(parent)}
                  </span>

                  <ChevronRight size={18} />
                </button>
              );
            })}
          </div>
        )}
      </section>

      {editing && selected && canManageParent && (
        <div className="modal-layer">
          <form
            className="task-create-modal panel"
            onSubmit={saveParent}
          >
            <header>
              <div>
                <p className="eyebrow">MODIFIER LE DOSSIER</p>
                <h2>{parentName(selected)}</h2>
              </div>

              <button
                type="button"
                className="drawer-close"
                onClick={() => setEditing(false)}
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
            </header>

            <div className="task-create-body">
              <label>
                Nom complet
                <input
                  value={form.fullName}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      fullName: event.target.value
                    })
                  }
                  required
                />
              </label>

              <div className="form-two-columns">
                <label>
                  Téléphone
                  <input
                    value={form.phone}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        phone: event.target.value
                      })
                    }
                  />
                </label>

                <label>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        email: event.target.value
                      })
                    }
                  />
                </label>
              </div>

              <div className="form-two-columns">
                <label>
                  CIN
                  <input
                    value={form.cin}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        cin: event.target.value
                      })
                    }
                  />
                </label>

                <label>
                  Profession
                  <input
                    value={form.profession}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        profession: event.target.value
                      })
                    }
                  />
                </label>
              </div>

              <label>
                Adresse
                <textarea
                  value={form.address}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      address: event.target.value
                    })
                  }
                />
              </label>

              {canAdminParentAccount && (
              <label>
                Statut d’activation
                <select
                  value={form.activationStatus}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      activationStatus: event.target.value
                    })
                  }
                >
                  <option value="pending_sms">
                    SMS à envoyer
                  </option>
                  <option value="sms_sent">
                    SMS envoyé
                  </option>
                  <option value="invited">Invité</option>
                  <option value="active">Actif</option>
                </select>
              </label>
              )}

              <div className="family-form-footer">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => setEditing(false)}
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  className="button button-primary"
                  disabled={saving}
                >
                  {saving
                    ? 'Enregistrement…'
                    : 'Enregistrer'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {selected && (
        <div
          className="drawer-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedId(null);
            }
          }}
        >
          <aside className="registration-drawer">
            <header className="drawer-header">
              <div>
                <p className="eyebrow">DOSSIER PARENT</p>
                <h2>{parentName(selected)}</h2>
              </div>

              <div className="drawer-header-actions">

  <button
    type="button"
    className="button button-secondary"
    onClick={deleteParent}
    disabled={saving}
    style={{
      color: '#b91c1c',
      borderColor: '#fecaca',
      background: '#fff1f2'
    }}
  >
    <Trash2 size={16} />
    {saving ? 'Suppression…' : 'Supprimer'}
  </button>

  <button
    type="button"
    className="button button-secondary"
    onClick={openEdit}
  >
    <Pencil size={16} />
    Modifier
  </button>

                <button
                  type="button"
                  className="drawer-close"
                  onClick={() => setSelectedId(null)}
                  aria-label="Fermer"
                >
                  <X size={20} />
                </button>
              </div>
            </header>

            <div className="drawer-scroll">
              <section className="detail-card">
                <h3>Coordonnées</h3>

                <dl className="detail-grid">
                  <div>
                    <dt>Téléphone</dt>
                    <dd>{selected.phone || '—'}</dd>
                  </div>

                  <div>
                    <dt>Email</dt>
                    <dd>{selected.email || '—'}</dd>
                  </div>

                  <div>
                    <dt>CIN</dt>
                    <dd>{selected.cin || '—'}</dd>
                  </div>

                  <div>
                    <dt>Profession</dt>
                    <dd>{selected.profession || '—'}</dd>
                  </div>

                  <div>
                    <dt>Adresse</dt>
                    <dd>{selected.address || '—'}</dd>
                  </div>

                  <div>
                    <dt>Activation</dt>
                    <dd>{activationLabel(selected)}</dd>
                  </div>

                  {canAdminParentAccount && <div>
                    <dt>Identifiant technique</dt>
                    <dd className="uid-value">
                      {selected.userUid ||
                        selected.authUid ||
                        'Non créé'}
                    </dd>
                  </div>}
                </dl>
              </section>

              <section className={`detail-card family-readiness-card ${familyReadiness.tone}`}>
                <div>
                  <p className="eyebrow">PRÉPARATION DE LA FAMILLE</p>
                  <h3>{familyReadiness.label}</h3>
                  <span>
                    {familyReadiness.total
                      ? `${familyReadiness.accepted} contrat${
                          familyReadiness.accepted > 1 ? 's' : ''
                        } accepté${
                          familyReadiness.accepted > 1 ? 's' : ''
                        } sur ${familyReadiness.total}`
                      : 'Aucun contrat à suivre pour le moment.'}
                  </span>
                </div>
              </section>

              <section className="detail-card">
                <h3>Enfants et contrats</h3>

                <div className="family-children-list contract-family-list">
                  {linkedChildren.length ? (
                    linkedChildren.map((child, index) => {
                      const contract = latestContractForStudent(child.id);
                      const meta = contractStatusMeta(contract);
                      const StatusIcon = meta.Icon;

                      return (
                        <article
                          className="parent-child-contract-row"
                          key={child.id}
                        >
                          <span>{index + 1}</span>

                          <div className="parent-child-contract-info">
                            <strong>{studentName(child)}</strong>
                            <small>{studentLevel(child)}</small>

                            <div className={`contract-status-line ${meta.tone}`}>
                              <StatusIcon size={15} />
                              <span>{meta.label}</span>
                            </div>

                            {contract && (
                              <small>
                                Total : {Number(contract.totalAmount || 0).toFixed(0)} DT
                                {contract.acceptedAt
                                  ? ` · Accepté le ${new Date(
                                      dateValue(contract.acceptedAt)
                                    ).toLocaleDateString('fr-FR')}`
                                  : ''}
                              </small>
                            )}
                          </div>

                          {canManageContracts && <button
                            type="button"
                            className="button button-primary parent-contract-button"
                            onClick={() => openContract(child, contract)}
                          >
                            <FileSignature size={16} />
                            {meta.action}
                          </button>}
                        </article>
                      );
                    })
                  ) : (
                    <div className="compact-empty">
                      Aucun enfant lié.
                    </div>
                  )}
                </div>
              </section>

              {selected.username && (
                <section className="detail-card">
                  <h3>Accès parent</h3>

                  <div className="activation-box">
                    <small>Nom d’utilisateur</small>
                    <strong>{selected.username}</strong>

                    <small>Statut</small>
                    <strong>
                      {activationLabel(selected)}
                    </strong>
                  </div>
                </section>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}